import {
  Badge,
  Button,
  Grid,
  Group,
  Modal,
  Progress,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  FaCalendar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileExcel,
  FaUpload,
} from "react-icons/fa";
import { v5 as uuidv5 } from "uuid";
import { read, utils, writeFile } from "xlsx";
import { ApiCrearTicket } from "../../../api/tickets";
import { ApiGetUsersByCodes } from "../../../api/usuarios";
import { buildRandomDateInMealHours, dateLocal } from "../../../utils/date";
const UUID_NAMESPACE = "1b671a64-40d5-491e-99b0-da01ff1f3341";

/** Normaliza encabezados para comparar (sin tildes, minúsculas, sin símbolos). */
const normalizeHeader = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findCodigoColumnKey = (row) => {
  if (!row || typeof row !== "object") return null;
  const keys = Object.keys(row);
  const scored = keys
    .map((key) => {
      const n = normalizeHeader(key);
      let score = 0;
      if (n === "codigo" || n === "code" || n === "cod") score = 100;
      else if (
        n.includes("codigotrabajador") ||
        n.includes("codigodeltrabajador")
      )
        score = 95;
      else if (n.includes("codigo") && n.includes("trab")) score = 90;
      else if (n.includes("codtrabajador") || n.includes("codtrab")) score = 85;
      else if (n.includes("codigo")) score = 70;
      else if (n.includes("code") && n.includes("trab")) score = 65;
      else if (n.includes("code")) score = 50;
      return { key, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored[0]?.key || null;
};

const getCodigoFromRow = (row, codigoKey) => {
  if (!row) return "";
  const raw = codigoKey ? row[codigoKey] : undefined;
  if (raw === undefined || raw === null || raw === "") return "";
  return String(raw).trim();
};

const BulkExcelUpload = ({ periodos, onSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedMeal, setSelectedMeal] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    dayjs(new Date()).tz("America/Guayaquil").format("YYYY-MM-DD")
  );

  const resetUploadForm = () => {
    setUploadProgress(0);
    setIsProcessing(false);
    setSelectedPeriod("");
    setSelectedMeal("");
    setSelectedDate(
      dayjs(new Date()).tz("America/Guayaquil").format("YYYY-MM-DD")
    );
  };

  const closeUploadModal = () => {
    if (isProcessing) return;
    resetUploadForm();
    setIsModalOpen(false);
  };

  const closeResultsModal = () => {
    setIsResultsOpen(false);
    setResults(null);
  };

  const downloadTemplate = () => {
    const data = [["Codigo Trabajador"], ["V001"], ["0093"]];
    const ws = utils.aoa_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Datos");
    writeFile(wb, "Plantilla_Codigos.xlsx");
  };

  const downloadErrors = () => {
    if (!results?.errors?.length) {
      toast.error("No hay errores para descargar");
      return;
    }
    const ws = utils.json_to_sheet(
      results.errors.map((err) => ({
        Fila: err.row,
        Codigo: err.codigo,
        Error: err.error,
      }))
    );
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Errores");
    writeFile(wb, "Errores_Carga_Tickets.xlsx");
  };

  const processFile = async (file) => {
    if (!file) return;
    if (!selectedPeriod || !selectedMeal || !selectedDate) {
      toast.error("Seleccione periodo, comida y fecha");
      return;
    }

    const periodo = periodos.find((p) => p.value === selectedPeriod);
    const comida = periodo?.comidas?.find(
      (c) => String(c.value) === String(selectedMeal)
    );

    if (!comida?.minTime || !comida?.maxTime) {
      toast.error(
        "La comida seleccionada no tiene horario (hora inicio/fin) definido"
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const wb = read(e.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const jsonData = utils.sheet_to_json(ws);

      if (!jsonData.length) {
        toast.error("El Excel no tiene filas de datos");
        return;
      }

      const codigoKey = findCodigoColumnKey(jsonData[0]);
      if (!codigoKey) {
        toast.error(
          "No se encontró columna de código. Use encabezados como: codigo, Código Trabajador, code, etc."
        );
        return;
      }

      const batchResults = {
        total: jsonData.length,
        success: 0,
        errors: [],
        errorCount: 0,
        params: {
          periodo: periodo?.label,
          comida: comida?.label,
          fecha: dayjs(selectedDate).format("DD/MM/YYYY"),
          horario: `${comida.minTime} - ${comida.maxTime}`,
          columnaCodigo: codigoKey,
        },
      };

      setIsProcessing(true);
      setUploadProgress(0);

      try {
        const codes = jsonData
          .map((row) => getCodigoFromRow(row, codigoKey))
          .filter(Boolean);
        const usersRes = await ApiGetUsersByCodes(codes);
        const usersByCode = new Map();
        if (usersRes.sms === "ok") {
          (usersRes.data || []).forEach((u) => {
            if (u.code) usersByCode.set(String(u.code).trim(), u);
            if (u.usuario) usersByCode.set(String(u.usuario).trim(), u);
          });
        }

        for (let i = 0; i < jsonData.length; i++) {
          try {
            const codigoStr = getCodigoFromRow(jsonData[i], codigoKey);
            if (!codigoStr) throw new Error("Código vacío");

            const usuarioDb = usersByCode.get(codigoStr);
            if (!usuarioDb) throw new Error("Código no existe");

            const usuario = {
              value: usuarioDb.pre_usuario_id,
              code: usuarioDb.code,
            };

            const isSpecialCode = [
              "V001",
              "E001",
              "G001",
              "P001",
              "X001",
              "S001",
              "T001",
            ].includes(codigoStr);

            // Hora aleatoria en el rango de la comida (si cruza medianoche, solo hasta 23:59:59)
            const fecha = buildRandomDateInMealHours(
              selectedDate,
              comida.minTime,
              comida.maxTime
            );

            const response = await ApiCrearTicket({
              pre_usuario_id: usuario.value,
              pre_periodo_id: selectedPeriod,
              pre_comida_id: selectedMeal,
              create_at: dateLocal(fecha),
              uuid4: uuidv5(
                `${usuario.value}-${selectedMeal}-${dayjs(selectedDate).format(
                  "YYYYMMDD"
                )}${isSpecialCode ? `-${Date.now()}` : ""}`,
                UUID_NAMESPACE
              ),
              manual: true,
            });

            if (response?.sms && response.sms !== "ok") {
              throw new Error(response.mensaje || "Error al crear ticket");
            }

            batchResults.success++;
          } catch (error) {
            batchResults.errorCount++;
            batchResults.errors.push({
              row: i + 2,
              codigo: getCodigoFromRow(jsonData[i], codigoKey),
              error: error.message || "Error inesperado",
            });
          }
          setUploadProgress(((i + 1) / jsonData.length) * 100);
        }

        setResults(batchResults);
        resetUploadForm();
        setIsModalOpen(false);
        setIsResultsOpen(true);

        if (batchResults.success > 0) {
          onSuccess?.();
        }

        if (batchResults.errorCount === 0) {
          toast.success(`Se crearon ${batchResults.success} tickets`);
        } else if (batchResults.success === 0) {
          toast.error("No se pudo crear ningún ticket");
        } else {
          toast(
            `Creados: ${batchResults.success}. Errores: ${batchResults.errorCount}`,
            { icon: "⚠️" }
          );
        }
      } finally {
        setIsProcessing(false);
        setUploadProgress(0);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Button leftIcon={<FaUpload />} onClick={() => setIsModalOpen(true)}>
        Cargar Excel Masivo
      </Button>

      <Modal
        opened={isModalOpen}
        onClose={closeUploadModal}
        size="xl"
        title="Carga Masiva de Tickets"
        closeOnClickOutside={!isProcessing}
        closeOnEscape={!isProcessing}
        withCloseButton={!isProcessing}
      >
        <Grid>
          <Grid.Col span={4}>
            <Select
              label="Periodo"
              data={periodos}
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              disabled={isProcessing}
              required
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <Select
              label="Comida"
              data={
                periodos.find((p) => p.value === selectedPeriod)?.comidas || []
              }
              value={selectedMeal}
              onChange={setSelectedMeal}
              disabled={!selectedPeriod || isProcessing}
              required
            />
          </Grid.Col>

          <Grid.Col span={4}>
            <TextInput
              label="Fecha"
              type="date"
              min={dayjs(
                periodos.find((p) => p.value === selectedPeriod)?.minDate
              ).format("YYYY-MM-DD")}
              max={dayjs(
                periodos.find((p) => p.value === selectedPeriod)?.maxDate
              ).format("YYYY-MM-DD")}
              icon={<FaCalendar size={14} />}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              disabled={!selectedPeriod || isProcessing}
              required
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Group position="center" my="xl">
              <Button
                leftIcon={<FaFileExcel />}
                variant="outline"
                onClick={downloadTemplate}
                disabled={isProcessing}
              >
                Descargar Plantilla
              </Button>

              <label htmlFor="upload-excel">
                <Button
                  component="span"
                  leftIcon={<FaUpload />}
                  disabled={
                    !selectedPeriod ||
                    !selectedMeal ||
                    !selectedDate ||
                    isProcessing
                  }
                  loading={isProcessing}
                >
                  {isProcessing ? "Procesando..." : "Seleccionar Excel"}
                  <input
                    id="upload-excel"
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    disabled={isProcessing}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      processFile(file);
                      e.target.value = "";
                    }}
                  />
                </Button>
              </label>
            </Group>
          </Grid.Col>

          {isProcessing && (
            <Grid.Col span={12}>
              <Progress value={uploadProgress} animate />
              <Text align="center" mt="md">
                Procesando {Math.round(uploadProgress)}%...
              </Text>
            </Grid.Col>
          )}
        </Grid>
      </Modal>

      <Modal
        opened={isResultsOpen}
        onClose={closeResultsModal}
        size="xl"
        title="Resultado de carga masiva"
      >
        {results && (
          <>
            <Group grow mb="md">
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                }}
              >
                <Text size="sm" color="dimmed">
                  Total filas
                </Text>
                <Text size="xl" weight={700}>
                  {results.total}
                </Text>
              </div>
              <div
                style={{
                  border: "1px solid #c3e6cb",
                  background: "#f0fff4",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                }}
              >
                <Group position="center" spacing={6}>
                  <FaCheckCircle color="#28a745" />
                  <Text size="sm" color="dimmed">
                    Creados
                  </Text>
                </Group>
                <Text size="xl" weight={700} color="green">
                  {results.success}
                </Text>
              </div>
              <div
                style={{
                  border: "1px solid #f5c6cb",
                  background: "#fff5f5",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                }}
              >
                <Group position="center" spacing={6}>
                  <FaExclamationTriangle color="#dc3545" />
                  <Text size="sm" color="dimmed">
                    Errores
                  </Text>
                </Group>
                <Text size="xl" weight={700} color="red">
                  {results.errorCount}
                </Text>
              </div>
            </Group>

            <Text size="sm" mb="xs">
              <strong>Periodo:</strong> {results.params.periodo}
            </Text>
            <Text size="sm" mb="xs">
              <strong>Comida:</strong> {results.params.comida}
            </Text>
            <Text size="sm" mb="xs">
              <strong>Fecha:</strong> {results.params.fecha}
            </Text>
            {results.params.horario && (
              <Text size="sm" mb="xs">
                <strong>Horario comida:</strong> {results.params.horario}
              </Text>
            )}
            <Text size="sm" mb="md">
              <strong>Columna detectada:</strong>{" "}
              <Badge variant="light">{results.params.columnaCodigo}</Badge>
            </Text>

            <Group position="apart" mb="sm">
              <Text weight={700}>Detalle de errores</Text>
              <Button
                leftIcon={<FaFileExcel />}
                variant="outline"
                size="xs"
                onClick={downloadErrors}
                disabled={!results.errors?.length}
              >
                Descargar errores
              </Button>
            </Group>

            {results.errors?.length > 0 ? (
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                <Table striped highlightOnHover>
                  <thead>
                    <tr>
                      <th>Fila</th>
                      <th>Código</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((err, i) => (
                      <tr key={`${err.row}-${i}`}>
                        <td>{err.row}</td>
                        <td>{err.codigo || "-"}</td>
                        <td style={{ color: "#dc3545" }}>{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <Text color="green" align="center" py="md">
                Todos los tickets se crearon correctamente.
              </Text>
            )}

            <Group position="right" mt="md">
              <Button onClick={closeResultsModal}>Cerrar</Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
};

export default BulkExcelUpload;
